import { Button, Input, InputNumber, Segmented, Select } from 'antd';
import { X } from 'lucide-react';
import { DASHBOARD_MAX_ROWS, DASHBOARD_TILES, TILE_SIZES } from '@pams/shared';
import type { DashboardShortcutTarget, TileSize } from '@pams/shared';
import { DASHBOARD_TILE_HINT_MAX } from '@pams/shared';
import { DASHBOARD_SHORTCUT_LABEL, DASHBOARD_TILE_LABEL, TILE_SIZE_LABEL } from '@/lib/labels';
import type { RowControls } from '@/components/ReorderableList';
import { LAYOUT_COPY } from '../copy';
import type { LayoutDraftEntry } from '../draft';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ONE TILE ON THE LAYOUT: its order controls, its width, and its words. [P6b]
 *
 *  ⚠️ EXACTLY ONE BOX. `ReorderableList layout="block"` renders a BARE `<li>`, so this card is
 *  the only object boundary there is — [D4.2]'s rule that the count is one, not zero and not
 *  two.
 *
 *  ⚠️ AND `onRemove` IS RENDERED HERE, NOT PASSED TO THE LIST. MEASURED against
 *  `ReorderableList`: in `'block'` layout it renders only `{handle, menu}` and the remove
 *  button lives in the `'row'` branch's trailing cluster — so `onRemove` on the list COMPILES,
 *  TYPE-CHECKS AND DOES NOTHING. `DutyPointStack` already works around it the same way.
 *
 *  ⚠️ `@container/tileentry`, NAMED. Unnamed it would resolve to `<main>` — 1062 content px at
 *  the 1366 floor even while this card has about 600 — so the two columns would pair inside a
 *  column that cannot hold them. `DutyPointCard`'s rule, verbatim.
 *
 *  ⚠️ AND §3b IS THE LAYOUT ITSELF: the hint sits BESIDE the override fields, at rest, always.
 *  Not in a tooltip — hover is unavailable on touch and unreachable from a keyboard, and
 *  `TileCard` already refuses a tooltip for the hint on a measured performance ground too.
 * ─────────────────────────────────────────────────────────────────────────────────
 */
export const TileEntryRow = ({
  entry,
  index,
  controls,
  reachableTargets,
  onChange,
  onRemove,
}: {
  entry: LayoutDraftEntry;
  index: number;
  controls: RowControls | null;
  /** The destinations the TARGET role could actually open. See the picker below. */
  reachableTargets: readonly DashboardShortcutTarget[];
  onChange: (patch: Partial<Omit<LayoutDraftEntry, 'tileId'>>) => void;
  onRemove: () => void;
}) => {
  const words = DASHBOARD_TILE_LABEL[entry.tileId];

  return (
    <article
      /*
       * ⚠️ [P6b.2] KEYED ON THE PLACEMENT, NEVER ON THE TILE. A repeatable tile can sit on the
       * layout several times, so a tile-keyed test id resolves to several nodes the moment the
       * feature this pass exists for is used — and `getByTestId` is strict, so the suite would
       * fail with a duplicate rather than with the defect. `data-tile` is the SECOND handle,
       * for a caller that means "any placement of this tile" (a freshly added one has a minted
       * id nothing outside the draft can predict).
       */
      data-testid={`layout-entry-${entry.instanceId}`}
      data-tile={entry.tileId}
      className="@container/tileentry flex flex-col gap-3 rounded-lg border border-border bg-card p-3"
    >
      <div className="flex items-center gap-2">
        {controls?.handle}
        <span className="w-5 text-right text-xs tabular-nums text-muted-foreground">{index + 1}</span>
        <h3 className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{words.title}</h3>

        {/*
          ⚠️ [P6b.2] WHICH ROW THIS SITS ON. A row ENDS where the operator says it ends, so
          this is the control that groups tiles — two placements given row 1 sit together even
          though a third would fit beside them.
        */}
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          {LAYOUT_COPY.row}
          <InputNumber
            size="small"
            className="w-16"
            min={1}
            max={DASHBOARD_MAX_ROWS}
            aria-label={`${LAYOUT_COPY.row}, for ${words.title}`}
            value={entry.row}
            onChange={(row) => {
              if (typeof row === 'number') onChange({ row });
            }}
          />
        </label>

        <Segmented<TileSize>
          size="small"
          aria-label={`${LAYOUT_COPY.width} of ${words.title}`}
          value={entry.size}
          options={TILE_SIZES.map((size) => ({ value: size, label: TILE_SIZE_LABEL[size] }))}
          onChange={(size) => {
            onChange({ size });
          }}
        />

        <Button
          type="text"
          size="small"
          danger
          aria-label={`Remove ${words.title} from this dashboard`}
          icon={<X className="size-4" aria-hidden />}
          onClick={onRemove}
        />
        {controls?.menu}
      </div>

      {/*
        ⚠️ [P6b.1] A SHORTCUT'S DESTINATION, AND IT IS THE ONE CONTROL HERE THAT CHANGES WHAT A
        TILE IS RATHER THAN WHAT IT IS CALLED. Every other field on this card is words; this
        one is the tile's whole content, which is why the catalogue cannot hold it.

        ⚠️ IT OFFERS ONLY DESTINATIONS THIS ROLE COULD OPEN. A shortcut to a page the target
        role bounces off is refused by the endpoint — so a picker that offered it would be a
        control whose only outcome is a 422, which is §2a's argument one level down.
      */}
      {DASHBOARD_TILES[entry.tileId].repeatable && (
        <label className="flex flex-col gap-1 text-xs text-muted-foreground">
          {LAYOUT_COPY.destination}
          <Select<DashboardShortcutTarget>
            size="small"
            value={entry.shortcutTarget ?? undefined}
            placeholder={LAYOUT_COPY.destinationPlaceholder}
            /* ⚠️ THE VISIBLE LABEL IS CONTAINED IN THE ACCESSIBLE NAME — WCAG 2.5.3, the rule
               `pwa`'s tab labels already follow. "Destination for X" would break it. */
            aria-label={`${LAYOUT_COPY.destination}, for ${words.title}`}
            options={reachableTargets.map((target) => ({
              value: target,
              label: DASHBOARD_SHORTCUT_LABEL[target],
            }))}
            onChange={(shortcutTarget) => {
              onChange({ shortcutTarget });
            }}
          />
        </label>
      )}

      <div className="grid gap-3 @min-[560px]/tileentry:grid-cols-2">
        <div className="flex flex-col gap-1">
          <p className="text-xs font-medium text-muted-foreground">{LAYOUT_COPY.whatThisCounts}</p>
          <p data-testid={`layout-hint-${entry.instanceId}`} className="text-xs text-muted-foreground">
            {words.hint}
          </p>
          <p className="text-xs text-muted-foreground/80">{LAYOUT_COPY.hintIsPartOfTheNumber}</p>
        </div>

        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            {LAYOUT_COPY.titleOverride}
            <Input
              size="small"
              /* ⚠️ THE REGISTRY WORD AS THE PLACEHOLDER — that IS what a blank field means. */
              placeholder={words.title}
              value={entry.titleOverride ?? ''}
              onChange={(event) => {
                onChange({ titleOverride: event.target.value });
              }}
            />
            <span className="text-[11px] text-muted-foreground/80">{LAYOUT_COPY.clearRestoresTitle}</span>
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            {LAYOUT_COPY.hintOverride}
            <Input.TextArea
              size="small"
              autoSize={{ minRows: 2, maxRows: 3 }}
              maxLength={DASHBOARD_TILE_HINT_MAX}
              /*
               * ⚠️ [C1e §3b] THE REGISTRY SENTENCE AS THE PLACEHOLDER **AND** THE CLEARING
               * SENTENCE BENEATH IT. On the two labels a placeholder alone says what a blank
               * field means; here it would LIE, because blank means hidden rather than default.
               */
              placeholder={words.hint}
              value={entry.hintOverride ?? ''}
              onChange={(event) => {
                onChange({ hintOverride: event.target.value });
              }}
            />
            <span className="text-[11px] text-muted-foreground/80">{LAYOUT_COPY.clearHidesHint}</span>
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            {LAYOUT_COPY.drillOverride}
            <Input
              size="small"
              placeholder={words.drill}
              value={entry.drillLabelOverride ?? ''}
              onChange={(event) => {
                onChange({ drillLabelOverride: event.target.value });
              }}
            />
          </label>
        </div>
      </div>
    </article>
  );
};
